import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export const pdfService = {
  // Generate contract PDF
  async generateContractPDF(contract, tenant, propertyUnit, companyLogo = null, companyInfo = null) {
    try {
      // Create a temporary div to render the contract content
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.top = '0';
      tempDiv.style.width = '650px'; // Reduce width further for better fit
      tempDiv.style.padding = '25px';
      tempDiv.style.fontFamily = 'Arial, sans-serif';
      tempDiv.style.fontSize = '10px';
      tempDiv.style.lineHeight = '1.4';
      tempDiv.style.backgroundColor = 'white';
      tempDiv.style.color = 'black';
      tempDiv.style.zIndex = '-9999';
      tempDiv.style.boxSizing = 'border-box';
      tempDiv.style.overflow = 'hidden';
      
      // Generate the contract HTML content
      tempDiv.innerHTML = this.generateContractHTML(contract, tenant, propertyUnit, companyLogo, companyInfo);
      
      // Add to document temporarily
      document.body.appendChild(tempDiv);
      
      // Wait longer for the content to render properly
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Convert to canvas with better rendering options
      const canvas = await html2canvas(tempDiv, {
        scale: 1, // Lower scale for smaller file size
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        width: 650,
        height: tempDiv.scrollHeight,
        scrollX: 0,
        scrollY: 0,
        windowWidth: 650,
        windowHeight: tempDiv.scrollHeight
      });
      
      // Remove temporary div
      document.body.removeChild(tempDiv);
      
      // Create PDF with margins
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 10; // 10mm margin on all sides
      const contentWidth = pdfWidth - margin * 2;
      const imgWidth = contentWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Add image to PDF with margin (single image, no multipage logic)
      pdf.addImage(
        canvas.toDataURL('image/png'),
        'PNG',
        margin,
        margin,
        imgWidth,
        imgHeight
      );
      
      return pdf;
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw new Error(`PDF generation failed: ${error.message}`);
    }
  },

  // Generate contract HTML content
  generateContractHTML(contract, tenant, propertyUnit, companyLogo, companyInfo) {
    const formatCurrency = (amount, currency = 'USD') => {
      const symbols = {
        USD: '$',
        EUR: '€',
        GBP: '£',
        AED: 'د.إ',
        SAR: 'ر.س',
        PKR: '₨',
        INR: '₹'
      };
      return `${symbols[currency] || '$'}${parseFloat(amount || 0).toLocaleString()}`;
    };

    const formatDate = (dateValue) => {
      if (!dateValue) return 'N/A';
      
      let date;
      if (dateValue.toDate) {
        // Firestore timestamp
        date = dateValue.toDate();
      } else if (typeof dateValue === 'string') {
        // Date string
        date = new Date(dateValue);
      } else if (dateValue instanceof Date) {
        // Date object
        date = dateValue;
      } else {
        return 'N/A';
      }
      
      if (isNaN(date.getTime())) return 'N/A';
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    };

    // Remove duplicate/extra data: Only show each section once, and avoid repeating contractId, company name, etc.
    // Ensure Financial Terms is only rendered once
    return `
      <div style="max-width: 650px; margin: 0 auto; font-family: Arial, sans-serif; box-sizing: border-box;">
        <!-- Header with Logo -->
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; border-bottom: 2px solid #333; padding-bottom: 15px;">
          <div>
            ${companyLogo ? `<img src="${companyLogo}" style="max-height: 80px; max-width: 200px;" alt="Company Logo">` : `<img src="/urproperty-logo.png" style="max-height: 80px; max-width: 200px;" alt="URProperty Logo">`}
            <h1 style="margin: 8px 0 3px 0; color: #333; font-size: 18px;">PROPERTY RENTAL CONTRACT</h1>
            <p style="margin: 0; color: #666; font-size: 12px;">
              ${companyInfo?.name || companyInfo?.companyName || 'Professional Property Management Services'}
            </p>
            ${companyInfo?.address ? `<p style="margin: 3px 0 0 0; color: #666; font-size: 10px;">${companyInfo.address}</p>` : ''}
            ${companyInfo?.phone ? `<p style="margin: 1px 0 0 0; color: #666; font-size: 10px;">Phone: ${companyInfo.phone}</p>` : ''}
            ${companyInfo?.email ? `<p style="margin: 1px 0 0 0; color: #666; font-size: 10px;">Email: ${companyInfo.email}</p>` : ''}
          </div>
          <div style="text-align: right;">
            <h3 style="margin: 0 0 3px 0; color: #333; font-size: 14px;">Contract ID</h3>
            <p style="margin: 0; font-weight: bold; color: #007bff; font-size: 14px;">${contract.contractId || contract.id}</p>
            <p style="margin: 3px 0 0 0; color: #666; font-size: 10px;">Date: ${formatDate(contract.createdAt)}</p>
          </div>
        </div>

        <!-- Contract Parties -->
        <div style="margin-bottom: 20px;">
          <h2 style="color: #333; border-bottom: 1px solid #ddd; padding-bottom: 8px; margin-bottom: 15px; font-size: 16px;">CONTRACT PARTIES</h2>
          <div style="display: flex; justify-content: space-between; margin-bottom: 15px;">
            <div style="flex: 1; margin-right: 20px;">
              <h4 style="color: #007bff; margin-bottom: 8px; font-size: 14px;">TENANT INFORMATION</h4>
              <p style="margin: 3px 0; font-size: 11px;"><strong>Name:</strong> ${tenant?.name || 'N/A'}</p>
              <p style="margin: 3px 0; font-size: 11px;"><strong>Email:</strong> ${tenant?.email || 'N/A'}</p>
              <p style="margin: 3px 0; font-size: 11px;"><strong>Phone:</strong> ${tenant?.phone || 'N/A'}</p>
              <p style="margin: 3px 0; font-size: 11px;"><strong>ID:</strong> ${tenant?.id || 'N/A'}</p>
            </div>
            <div style="flex: 1;">
              <h4 style="color: #007bff; margin-bottom: 8px; font-size: 14px;">PROPERTY INFORMATION</h4>
              <p style="margin: 3px 0; font-size: 11px;"><strong>Unit Number:</strong> ${propertyUnit?.unitNumber || 'N/A'}</p>
              <p style="margin: 3px 0; font-size: 11px;"><strong>Unit Type:</strong> ${propertyUnit?.unitType || 'N/A'}</p>
              <p style="margin: 3px 0; font-size: 11px;"><strong>Bedrooms:</strong> ${propertyUnit?.bedrooms || 'Studio'}</p>
              <p style="margin: 3px 0; font-size: 11px;"><strong>Bathrooms:</strong> ${propertyUnit?.bathrooms || 'N/A'}</p>
            </div>
          </div>
        </div>

        <!-- Contract Terms -->
        <div style="margin-bottom: 20px;">
          <h2 style="color: #333; border-bottom: 1px solid #ddd; padding-bottom: 8px; margin-bottom: 15px; font-size: 16px;">CONTRACT TERMS</h2>
          <div style="display: flex; justify-content: space-between; margin-bottom: 15px;">
            <div style="flex: 1; margin-right: 20px;">
              <h4 style="color: #007bff; margin-bottom: 8px; font-size: 14px;">LEASE PERIOD</h4>
              <p style="margin: 3px 0; font-size: 11px;"><strong>Start Date:</strong> ${formatDate(contract.contractStartDate)}</p>
              <p style="margin: 3px 0; font-size: 11px;"><strong>End Date:</strong> ${formatDate(contract.contractEndDate)}</p>
              <p style="margin: 3px 0; font-size: 11px;"><strong>Duration:</strong> ${contract.contractDuration || 'N/A'}</p>
            </div>
            <div style="flex: 1;">
              <h4 style="color: #007bff; margin-bottom: 8px; font-size: 14px;">CONTRACT STATUS</h4>
              <p style="margin: 3px 0; font-size: 11px;"><strong>Status:</strong> <span style="color: ${contract.status === 'active' ? '#28a745' : '#dc3545'}; font-weight: bold;">${contract.status?.toUpperCase() || 'N/A'}</span></p>
              <p style="margin: 3px 0; font-size: 11px;"><strong>Currency:</strong> ${contract.currency || 'USD'}</p>
            </div>
          </div>
        </div>

        <!-- Financial Terms (only once) -->
        <div style="margin-bottom: 20px;">
          <h2 style="color: #333; border-bottom: 1px solid #ddd; padding-bottom: 8px; margin-bottom: 15px; font-size: 16px;">FINANCIAL TERMS</h2>
          <table style="width: 100%; border-collapse: collapse; margin-bottom: 15px; font-size: 11px; table-layout: fixed;">
            <thead>
              <tr style="background-color: #f8f9fa;">
                <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; width: 60%;">Description</th>
                <th style="border: 1px solid #ddd; padding: 8px; text-align: right; font-size: 12px; width: 40%;">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style="border: 1px solid #ddd; padding: 8px;"><strong>Total Rental Amount</strong></td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: right;"><strong>${formatCurrency(contract.totalRentalAmount, contract.currency)}</strong></td>
              </tr>
              <tr>
                <td style="border: 1px solid #ddd; padding: 8px;">Monthly Rent</td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">${formatCurrency(contract.monthlyRent || (contract.totalRentalAmount && contract.contractDuration ? contract.totalRentalAmount / contract.contractDuration : contract.totalRentalAmount / 12), contract.currency)}</td>
              </tr>
              <tr>
                <td style="border: 1px solid #ddd; padding: 8px;">Installment Amount (${contract.numberOfCheques} cheques)</td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">${formatCurrency(contract.chequeAmount, contract.currency)}</td>
              </tr>
              <tr style="background-color: #f8f9fa;">
                            <td style="border: 1px solid #ddd; padding: 8px;"><strong>Admin Fees (${contract.brokeragePercentage}%)</strong></td>
            <td style="border: 1px solid #ddd; padding: 8px; text-align: right;"><strong>${formatCurrency(contract.brokerageFee, contract.currency)}</strong></td>
              </tr>
              <tr style="background-color: #e9ecef;">
                <td style="border: 1px solid #ddd; padding: 8px;"><strong>TOTAL CONTRACT AMOUNT</strong></td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: right;"><strong style="font-size: 14px;">${formatCurrency(contract.totalAmount, contract.currency)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Additional Terms -->
        <div style="margin-bottom: 20px;">
          <h2 style="color: #333; border-bottom: 1px solid #ddd; padding-bottom: 8px; margin-bottom: 15px; font-size: 16px;">ADDITIONAL TERMS</h2>
          <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px;">
            <h4 style="color: #007bff; margin-bottom: 10px; font-size: 14px;">Important Notes:</h4>
            <ul style="margin: 0; padding-left: 20px; font-size: 11px;">
              <li style="margin-bottom: 3px;">This contract is legally binding and enforceable under local property laws.</li>
              <li style="margin-bottom: 3px;">All payments must be made on time as specified in the payment schedule.</li>
              <li style="margin-bottom: 3px;">The tenant is responsible for maintaining the property in good condition.</li>
              <li style="margin-bottom: 3px;">Any modifications to this contract must be agreed upon in writing by both parties.</li>
              <li style="margin-bottom: 3px;">This contract automatically terminates on the end date unless renewed.</li>
            </ul>
          </div>
        </div>

        <!-- Notes Section -->
        ${contract.notes ? `
        <div style="margin-bottom: 20px;">
          <h2 style="color: #333; border-bottom: 1px solid #ddd; padding-bottom: 8px; margin-bottom: 15px; font-size: 16px;">SPECIAL NOTES</h2>
          <div style="background-color: #fff3cd; padding: 12px; border-left: 4px solid #ffc107; border-radius: 3px;">
            <p style="margin: 0; font-style: italic; font-size: 11px;">${contract.notes}</p>
          </div>
        </div>
        ` : ''}

        <!-- Footer -->
        <div style="margin-top: 30px; padding-top: 15px; border-top: 2px solid #333; text-align: center;">
          <p style="margin: 0; color: #666; font-size: 10px;">
            This document was generated by the Property Management System on ${new Date().toLocaleDateString()}
          </p>
          <p style="margin: 3px 0 0 0; color: #666; font-size: 10px;">
            For any questions or concerns, please contact your property manager.
          </p>
        </div>
      </div>
    `;
  },

  // Download PDF
  downloadPDF(pdf, filename) {
    try {
      if (!pdf) {
        throw new Error('PDF object is null or undefined');
      }
      
      if (!filename) {
        filename = `contract_${new Date().toISOString().split('T')[0]}.pdf`;
      }
      
      // Save the PDF
      pdf.save(filename);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      throw new Error(`PDF download failed: ${error.message}`);
    }
  }
}; 